import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Fade, Grow, IconButton, Menu, MenuItem, useTheme } from '@mui/material';
import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import MenuItemText from '../MenuItemText';
import MobileMenuItem from './MobileMenuItem';
import { AppMenuItem } from './types';

interface MobileMenuProps {
    appMenuItems: AppMenuItem[];
    adminMenuItems: AppMenuItem[];
    userIsAuthenticated: boolean;
}

/**
 * Mobile navigation menu (visible on small screens)
 */
const MobileMenu: FC<MobileMenuProps> = ({ appMenuItems, adminMenuItems, userIsAuthenticated }) => {
    const theme = useTheme();
    const [anchorElNav, setAnchorElNav] = useState<EventTarget | any>(null);
    const [anchorElAdmin, setAnchorElAdmin] = useState<EventTarget | any>(null);

    const handleOpenNavMenu = (event: React.SyntheticEvent<EventTarget>) => {
        setAnchorElNav(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleOpenAdminMenu = (event: React.SyntheticEvent<EventTarget>) => {
        setAnchorElAdmin(event.currentTarget);
    };

    const handleCloseAdminMenu = () => {
        setAnchorElAdmin(null);
    };

    return (
        <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
                sx={{
                    position: 'relative',
                    '& .MuiSvgIcon-root': {
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out',
                    }
                }}
            >
                {/* Hamburger menu icon - visible when closed */}
                <MenuIcon
                    sx={{
                        position: 'absolute',
                        transform: Boolean(anchorElNav) ? 'rotate(90deg) scale(0)' : 'rotate(0deg) scale(1)',
                        opacity: Boolean(anchorElNav) ? 0 : 1,
                    }}
                />
                {/* Close X icon - visible when open */}
                <CloseIcon
                    sx={{
                        position: 'absolute',
                        transform: Boolean(anchorElNav) ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0)',
                        opacity: Boolean(anchorElNav) ? 1 : 0,
                    }}
                />
            </IconButton>
            <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                keepMounted
                className='app-menu-root'
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                TransitionComponent={Grow}
                transitionDuration={{ enter: 300, exit: 200 }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiPaper-root': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#999',
                        backdropFilter: 'blur(8px)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'}`,
                        marginTop: '8px',
                    },
                    '& .MuiList-root': {
                        backgroundColor: 'transparent',
                        padding: '8px 0',
                    },
                    '& .MuiMenuItem-root': {
                        margin: '2px 8px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.15)',
                            transform: 'translateX(4px)',
                        }
                    }
                }}
            >
                {appMenuItems.filter(x => !x.RequireAdmin).map((menuItem, index) => (
                    <MobileMenuItem
                        key={menuItem.name}
                        menuItem={menuItem}
                        onCloseNavMenu={handleCloseNavMenu}
                        index={index}
                    />
                ))}

                {userIsAuthenticated && adminMenuItems.length > 0 && (
                    <>
                        <Fade in timeout={500}>
                            <MenuItem
                                onClick={handleOpenAdminMenu}
                                sx={{
                                    my: 2,
                                    display: 'block'
                                }}
                            >
                                <MenuItemText context="dropdown">Admin</MenuItemText>
                            </MenuItem>
                        </Fade>
                        <Menu
                            anchorEl={anchorElAdmin}
                            open={Boolean(anchorElAdmin)}
                            onClose={handleCloseAdminMenu}
                            TransitionComponent={Grow}
                            transitionDuration={{ enter: 250, exit: 150 }}
                            sx={{
                                '& .MuiPaper-root': {
                                    backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#999',
                                    backdropFilter: 'blur(8px)',
                                    borderRadius: '12px',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'}`,
                                },
                                '& .MuiMenuItem-root': {
                                    margin: '2px 8px',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.15)',
                                        transform: 'translateX(4px)',
                                    }
                                }
                            }}
                        >
                            {adminMenuItems.map((adminItem, index) => (
                                <Fade in timeout={200 + (index * 30)} key={adminItem.name}>
                                    <MenuItem
                                        onClick={() => {
                                            handleCloseAdminMenu();
                                            handleCloseNavMenu();
                                        }}
                                        component={Link}
                                        to={adminItem.link}
                                    >
                                        <MenuItemText context="dropdown">
                                            {adminItem.name}
                                        </MenuItemText>
                                    </MenuItem>
                                </Fade>
                            ))}
                        </Menu>
                    </>
                )}
            </Menu>
        </Box>
    );
};

export default MobileMenu;
